// DashboardDataProvider.tsx
import { useEffect, useState } from "react";

import Dashboard from "./Dashboard";
import {
  useDashboardStore,
  useOrganizationSettingsStore,
  useUserSettingsStore,
} from "@/stores";
import { useAuth, useUser } from "@clerk/react";
import { useLocation } from "react-router-dom";
import { UsageLimitProvider } from "@/hooks/UsageLimitContext";
import UpgradeOverlay from "@/components/onboarding/upgrade-overlay";
import { useTrialExpired } from "@/hooks/onboarding";
import { useComplianceRole } from "@/hooks/IsAdminContext";
import { isRepopulatingCampaign } from "@/lib/eve";
import { isRefreshingCreators } from "@/lib/creatorRefresh";
import type { CreatorRefresh } from "@/types/types";
import { AppLoading, useSplashHold } from "@/components/app-loading";

export default function DashboardDataProvider() {
  const { fetchAllData, fetchCampaigns, fetchTasks, fetchUsage, campaigns } =
    useDashboardStore();
  const { fetchUserInfo } = useUserSettingsStore();
  const { getToken, orgId } = useAuth();
  const { fetchOrganizationInfo } = useOrganizationSettingsStore();
  const pathname = useLocation().pathname;
  // Compliance reviewers only get the tasks feed — every other endpoint
  // 403s for them server-side, so don't even ask.
  const { isCompliance, isLoaded: roleLoaded } = useComplianceRole();

  const { user } = useUser();

  // The first-load splash stays up until the first fetch settles, so the
  // dashboard appears with its campaigns, tasks and settings already in.
  const [firstLoad, setFirstLoad] = useState(true);
  useSplashHold(firstLoad);

  const fetchSettings = async (
    orgId: string,
    userId: string,
    token: string
  ) => {
    try {
      // Fetch organization and user settings
      await Promise.all([
        fetchOrganizationInfo(orgId, userId, token),
        fetchUserInfo(orgId, userId, token),
      ]);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      if (!user?.id || !roleLoaded) {
        return;
      }
      try {
        if (!orgId) return;
        const token = await getToken();
        if (!token) return;

        if (isCompliance) {
          await fetchTasks(orgId, token);
          return;
        }

        // Settings, tasks, campaigns, usage and Dub domains, together.
        await Promise.all([fetchSettings(orgId, user.id, token), fetchAllData(orgId, token)]);
      } catch (error) {
        console.error("Error initializing dashboard data:", error);
      } finally {
        setFirstLoad(false);
      }
    };

    initializeData();
  }, [
    orgId,
    user?.id,
    fetchAllData,
    fetchTasks,
    getToken,
    fetchOrganizationInfo,
    fetchUserInfo,
    isCompliance,
    roleLoaded,
  ]);

  // A freshly created campaign (wizard or Amy chat) sits in POPULATING —
  // shown disabled with a spinner in the campaigns nav — until the search
  // worker fills it. Amy re-running the search on an EXISTING campaign
  // stamps repopulating_since instead (non-blocking "Finding creators"
  // indicator). A launch with "Refresh creator data first" stamps
  // creator_refresh the same way (the creators toolbar shows the progress
  // and the worker launches when done). While any exists, poll so the UI
  // updates the moment it lands, without a manual page refresh. Self-stops.
  const hasPopulating =
    campaigns?.some(
      (c: {
        status?: string;
        repopulating_since?: string | null;
        creator_refresh?: CreatorRefresh | null;
      }) =>
        c?.status === "POPULATING" ||
        isRepopulatingCampaign(c) ||
        isRefreshingCreators(c)
    ) ?? false;
  useEffect(() => {
    if (!hasPopulating || !orgId) return;
    const interval = setInterval(() => {
      void (async () => {
        const token = await getToken();
        if (token) await fetchCampaigns(orgId, token);
      })();
    }, 30_000);
    return () => clearInterval(interval);
  }, [hasPopulating, orgId, getToken, fetchCampaigns]);

  const refreshPathnames = [
    "/dashboard/home",
    "/dashboard/campaigns",

    "/dashboard/settings",
  ];

  useEffect(() => {
    if (!refreshPathnames.includes(pathname)) return;
    if (!orgId || !user?.id || !getToken) return; // guard early

    const refreshPathData = async () => {
      const token = await getToken();
      if (!token) return;

      const pathname_stripped = pathname.replace("/dashboard/", "");

      if (pathname_stripped === "home") {
        await fetchTasks(orgId, token);
      } else if (pathname_stripped === "campaigns") {
        await fetchCampaigns(orgId, token);
      } else if (pathname_stripped === "settings") {
        await fetchSettings(orgId, user.id, token);
      }

      await fetchUsage(token);
    };

    refreshPathData();
  }, [pathname, orgId, user?.id, getToken]);

  return <Dashboard />;
}

export function DashboardWithUsageLimit() {
  const { trialExpired, isLoaded } = useTrialExpired();
  const [showUpgrade, setShowUpgrade] = useState(true);

  if (!isLoaded) return <AppLoading />;

  return (
    <UsageLimitProvider>
      {isLoaded && trialExpired ? (
        <UpgradeOverlay open={showUpgrade} onOpenChange={setShowUpgrade} />
      ) : null}

      <DashboardDataProvider />
    </UsageLimitProvider>
  );
}
